import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useState,
} from "react";
import cx from "classnames";

import styles from "./Dialog.module.scss";
import { useDialogContext } from "./internal/useDialogContext";

/**
 * Props for the DialogContent component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type DialogContentProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * When true, the content will stick below the header during scroll.
   * @default false
   */
  sticky?: boolean;
};

/**
 * DialogContent component for the main content area of a Dialog.
 *
 * Features:
 * - Main content container for dialog body
 * - Flexible height with overflow handling
 * - Optional sticky positioning below header
 * - Consistent padding and spacing
 * - Responsive design
 * - Proper content scrolling for long content
 *
 * @example
 * // Basic content
 * <Dialog.Content>
 *   <p>This is the main content of the dialog.</p>
 * </Dialog.Content>
 *
 * @example
 * // Sticky content (e.g., search field that remains visible while scrolling)
 * <Dialog.Content sticky>
 *   <TextField placeholder="Filter results..." />
 * </Dialog.Content>
 */
export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (props, ref) => {
    const { children, className, sticky = false, ...rest } = props;
    const { headerRef } = useDialogContext();
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
      const headerElement = headerRef?.current;
      if (!sticky || !headerElement) return;

      const updateHeight = () => {
        setHeaderHeight(headerElement.offsetHeight || 0);
      };

      updateHeight();
      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(headerElement);

      return () => resizeObserver.disconnect();
    }, [sticky, headerRef]);

    const contentClassNames = cx(styles["content"], className, {
      [styles["sticky"]]: sticky,
    });

    const stickyStyle = sticky ? { top: `${headerHeight}px` } : undefined;

    return (
      <div
        className={contentClassNames}
        data-anv="dialog-content"
        ref={ref}
        style={{ ...stickyStyle, ...rest.style }}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

DialogContent.displayName = "DialogContent";
